import { formatLocalDate } from '@/utils/localDateUtils';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import reciboService from '@/services/reciboService';
import dgiService from '@/services/dgiService';
import { Recibo } from '@/types/recibo';
import { CfeDocument, CfeTipoLabel } from '@/types/cfe';
import { useAuth } from '@/contexts/AuthContext';
import { useDialog } from '@/hooks/useDialog';
import { formatReceiptAmount } from '@/utils/currencyUtils';
import CfeStatusBadge from '../dgi/CfeStatusBadge';
import CfeDetailModal from '../dgi/CfeDetailModal';
import { getCfeRequestError, parseCfeStatusResponse } from '@/utils/cfeUtils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  recibo: Recibo | null;
  onReciboUpdated?: () => void;
};

export default function ReciboDetailModal({ isOpen, onClose, recibo, onReciboUpdated }: Props) {
  const { hasPermission } = useAuth();
  const { showAlert, showConfirm } = useDialog();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cfeDocuments, setCfeDocuments] = useState<CfeDocument[]>([]);
  const [cfeLoading, setCfeLoading] = useState(false);
  const [cfeLoadError, setCfeLoadError] = useState<string | null>(null);
  const [cfeLoadedReceipt, setCfeLoadedReceipt] = useState<number | null>(null);
  const cfeRequestVersion = useRef(0);
  const [cfeGenerating, setCfeGenerating] = useState(false);
  const [cfeGenerateResult, setCfeGenerateResult] = useState<{
    success: boolean;
    has_warnings?: boolean;
    message: string;
    documents: Array<{
      success: boolean;
      message: string;
      cfe_tipo?: string;
      warning?: string | null;
      raw_error?: string | null;
      error?: string | null;
    }>;
  } | null>(null);
  const [selectedCfeDoc, setSelectedCfeDoc] = useState<CfeDocument | null>(null);
  const [showCfeDetail, setShowCfeDetail] = useState(false);

  const [resguardoForm, setResguardoForm] = useState({ confirmado: false, numero: '' });
  useEffect(() => {
    setResguardoForm({ confirmado: !!recibo?.resguardo_confirmado, numero: recibo?.resguardo_numero ?? '' });
  }, [recibo?.id, recibo?.resguardo_confirmado, recibo?.resguardo_numero, isOpen]);

  const saveResguardo = async () => {
    if (!recibo) return;
    setSubmitting(true);
    try {
      await reciboService.updateTenantResguardo(Number(recibo.id), resguardoForm.confirmado, resguardoForm.numero);
      onReciboUpdated?.();
      onClose();
      showAlert({ type: 'success', title: 'Resguardo guardado', message: 'Se actualizó el resguardo del inquilino.' });
    } catch (error: any) {
      showAlert({ type: 'error', title: 'No se pudo guardar', message: error?.response?.data?.message || 'Revise el número de resguardo.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Load CFE documents when recibo changes
  useEffect(() => {
    setCfeDocuments([]);
    setCfeLoadError(null);
    setCfeLoadedReceipt(null);
    setCfeGenerateResult(null);
    if (recibo && isOpen) {
      loadCfeDocuments();
    }
    return () => { cfeRequestVersion.current++; };
  }, [recibo?.id, isOpen]);

  const loadCfeDocuments = async () => {
    if (!recibo) return;
    const version = ++cfeRequestVersion.current;
    const receiptId = Number(recibo.id);
    setCfeLoading(true);
    setCfeLoadError(null);
    setCfeLoadedReceipt(null);
    try {
      const res = await dgiService.getStatus(receiptId);
      const documents = parseCfeStatusResponse(res);
      if (version !== cfeRequestVersion.current) return;
      setCfeDocuments(documents);
      setCfeLoadedReceipt(receiptId);
    } catch (error) {
      if (version !== cfeRequestVersion.current) return;
      // Retain any previously loaded documents; a failed refresh does not prove they are absent.
      setCfeLoadError(getCfeRequestError(error, 'No se pudieron cargar los CFE de este recibo'));
    } finally {
      if (version === cfeRequestVersion.current) setCfeLoading(false);
    }
  };

  const handleGenerateCfe = async () => {
    if (!recibo || cfeLoading || cfeLoadError || cfeLoadedReceipt !== Number(recibo.id) || cfeDocuments.length > 0) return;
    const confirmed = await showConfirm({
      title: 'Generar CFE',
      message: '¿Generar documentos CFE para este recibo?',
      type: 'info',
    });
    if (!confirmed) return;

    setCfeGenerating(true);
    setCfeGenerateResult(null);
    try {
      const result = await dgiService.generar(Number(recibo.id));
      const res = (result as any);
      setCfeGenerateResult(res);

      // Show a dialog for any CFE type that failed (eTicket, Resguardo, etc.)
      const anyFailures = Array.isArray(res?.documents)
        ? res.documents.filter((doc: any) => !doc?.success)
        : [];

      if (anyFailures.length > 0) {
        for (const doc of anyFailures) {
          // Prefer the raw TA-FACE error; fall back to translated message
          const rawError = doc?.raw_error?.trim?.() || '';
          const translatedError = doc?.error?.trim?.() || doc?.message?.trim?.() || '';
          const detail = rawError
            ? `TA-FACE: ${rawError}${translatedError && translatedError !== rawError ? `\n\nDetalle: ${translatedError}` : ''}`
            : translatedError || 'Error desconocido';
          const tipoLabel = doc?.cfe_tipo ? `(${doc.cfe_tipo}) ` : '';
          console.error(`Error generating ${doc?.cfe_tipo ?? 'CFE'}:`, {
            reciboId: Number(recibo.id),
            detail,
            doc,
            response: res,
          });
          await showAlert({
            type: 'error',
            title: `Error al generar comprobante ${tipoLabel}`,
            message: detail,
          });
        }
      }

      loadCfeDocuments();
    } catch (error) {
      console.error('Error generating CFE:', error);
      setCfeGenerateResult({
        success: false,
        message: 'Error de conexión al generar el comprobante. Intente nuevamente.',
        documents: [],
      });
    } finally {
      setCfeGenerating(false);
    }
  };

  if (!recibo) return null;

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return formatLocalDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number, moneda: string) => {
    return formatReceiptAmount(amount, moneda);
  };

  const getEstadoBadge = (estado: string) => {
    const colors = {
      ACTIVO: 'bg-green-100 text-green-800 border-green-200',
      CANCELADO: 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[estado as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleDownloadPDF = async () => {
    try {
      const blob = await reciboService.downloadReciboPDF(Number(recibo.id));
      
      // If blob is null, it means a new window was opened for printing
      if (!blob) {
        return;
      }
      
      // Download PDF blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recibo-${recibo.numero_recibo}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al descargar el PDF del recibo',
      });
    }
  };

  const handleCancelRecibo = async () => {
    if (!cancelMotivo.trim()) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Por favor ingrese el motivo de anulación',
      });
      return;
    }

    if (cancelMotivo.trim().length < 10) {
      showAlert({
        type: 'warning',
        title: 'Motivo muy corto',
        message: 'El motivo de anulación debe tener al menos 10 caracteres',
      });
      return;
    }

    if (cancelMotivo.length > 500) {
      showAlert({
        type: 'warning',
        title: 'Motivo muy largo',
        message: 'El motivo de anulación no puede exceder 500 caracteres',
      });
      return;
    }

    const confirmed = await showConfirm({
      title: 'Anular Recibo',
      message: '¿Está seguro que desea anular este recibo?',
      type: 'danger',
      confirmText: 'Sí, anular',
    });
    if (!confirmed) return;

    setSubmitting(true);
    try {
      await reciboService.cancelRecibo(Number(recibo.id), { motivo: cancelMotivo });
      showAlert({
        type: 'success',
        title: 'Éxito',
        message: 'Recibo cancelado exitosamente',
      });
      setShowCancelModal(false);
      setCancelMotivo('');
      if (onReciboUpdated) {
        onReciboUpdated();
      }
      onClose();
    } catch (error) {
      console.error('Error canceling receipt:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al cancelar el recibo',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToPago = () => {
    navigate(`/payments/${recibo.pago_id}`);
    onClose();
  };

  const handleGoToContrato = () => {
    navigate(`/contracts/${recibo.contrato_id}`);
    onClose();
  };

  // Determine if this is a partial payment based on monto_aplicado_pago (if available) or monto_recibo
  // For receipts with cost breakdown, use monto_aplicado_pago to determine if payment is partial
  const amountApplied = recibo.monto_aplicado_pago ?? recibo.monto_recibo;
  const isPartialPayment = amountApplied < recibo.monto_total_pago;

  // Parse desglose JSON if available
  const parseDesglose = () => {
    if (!recibo.desglose) return null;

    try {
      return JSON.parse(recibo.desglose);
    } catch (error) {
      console.error('Error parsing desglose:', error);
      return null;
    }
  };

  const desglose = parseDesglose();
  const irpfDescontadoEnNeto = desglose?.retencion_irpf_descontada_en_neto !== false;
  const receiptTotal = formatReceiptAmount(recibo.monto_recibo, recibo.moneda);

  return (
    <>
      <Modal isOpen={isOpen && !showCancelModal} onClose={onClose} title="Detalle del Recibo" size="lg">
        <div className="space-y-4">
          {/* Header with Status */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{recibo.numero_recibo}</h3>
              <p className="text-sm text-gray-500">ID: {recibo.id}</p>
            </div>
            <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${getEstadoBadge(recibo.estado)}`}>
              {recibo.estado}
            </span>
          </div>

          {/* Payment Information */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Información del Pago</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Tipo de Pago</p>
                <p className="text-sm font-medium text-gray-900">{recibo.tipo_pago}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  {recibo.tipo_pago === 'INQUILINO' ? 'Inquilino' : 'Propietario'}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {recibo.tipo_pago === 'INQUILINO' 
                    ? (recibo.pago?.inquilino_nombre || recibo.contrato?.inquilino_nombre || '-')
                    : (recibo.pago?.propietario_nombre || '-')
                  }
                  {!!recibo.pago?.es_pago_parcial && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title={`${recibo.pago.dias_ocupados || 0} días`}>
                      Parcial
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Método de Pago</p>
                <p className="text-sm font-medium text-gray-900">{recibo.metodo_pago || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Monto del Recibo</p>
                <p className="text-sm font-medium text-gray-900">{receiptTotal}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Monto Total del Pago</p>
                <p className="text-sm font-medium text-gray-900">{formatCurrency(recibo.monto_total_pago, recibo.moneda)}</p>
              </div>
            </div>
            {isPartialPayment && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Pago Parcial:</span> Este recibo representa un pago parcial.
                  El monto restante es {formatCurrency(recibo.monto_total_pago - amountApplied, recibo.moneda)}.
                </p>
              </div>
            )}
          </div>

          {/* Cost Breakdown (if available) */}
          {desglose && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">
                {recibo.tipo_pago === 'INQUILINO' ? 'Desglose de Costos' : 'Desglose de Deducciones'}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                {recibo.tipo_pago === 'INQUILINO' ? (
                  /* INQUILINO: Show extra costs breakdown */
                  <div className="space-y-2">
                    {desglose.monto_alquiler !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">Monto de Alquiler (Base):</span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(desglose.monto_alquiler, recibo.moneda)}
                        </span>
                      </div>
                    )}
                    {desglose.interes_mora > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">Interés por Mora:</span>
                        <span className="text-sm font-medium text-red-600">
                          + {formatCurrency(desglose.interes_mora, recibo.moneda)}
                        </span>
                      </div>
                    )}
                    {desglose.iva_interes > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">IVA sobre Interés:</span>
                        <span className="text-sm font-medium text-red-600">
                          + {formatCurrency(desglose.iva_interes, recibo.moneda)}
                        </span>
                      </div>
                    )}
                    {desglose.retencion_irpf > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">Retención IRPF:</span>
                        <span className="text-sm font-medium text-red-600">
                          {desglose.agente_retencion_inquilino ? '-' : '+'} {formatCurrency(desglose.retencion_irpf, recibo.moneda)}
                        </span>
                      </div>
                    )}
                    {Array.isArray(desglose.otros_importes) && desglose.otros_importes.length > 0 && (
                      <div className="space-y-1">
                        {desglose.otros_importes.map((item: any, idx: number) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-sm text-gray-700">{item.descripcion}:</span>
                            <span className="text-sm font-medium text-red-600">
                              + {formatCurrency(item.valor, recibo.moneda)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-gray-300 mt-2 pt-2 flex justify-between">
                      <span className="text-sm font-semibold text-gray-900">Total a Pagar:</span>
                      <span className="text-sm font-bold text-gray-900">
                        {receiptTotal}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* PROPIETARIO: Show deductions breakdown */
                  <div className="space-y-2">
                    {desglose.valor_alquiler !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">Alquiler (base IRPF):</span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(desglose.valor_alquiler, recibo.moneda)}
                        </span>
                      </div>
                    )}
                    {desglose.comision_administracion > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">Comisión de Administración:</span>
                        <span className="text-sm font-medium text-red-600">
                          - {formatCurrency(desglose.comision_administracion, recibo.moneda)}
                        </span>
                      </div>
                    )}
                    {desglose.iva_comision > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">IVA sobre Comisión:</span>
                        <span className="text-sm font-medium text-red-600">
                          - {formatCurrency(desglose.iva_comision, recibo.moneda)}
                        </span>
                      </div>
                    )}
                    {desglose.retencion_irpf_inquilino > 0 && (
                      <div className="flex justify-between"><span className="text-sm">IRPF retenido por inquilino:</span><span>- {formatCurrency(desglose.retencion_irpf_inquilino, recibo.moneda)}</span></div>
                    )}
                    {desglose.retencion_irpf > 0 && (
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">
                            {irpfDescontadoEnNeto ? 'Retención IRPF:' : 'Retención IRPF (retenida por la inmobiliaria):'}
                          </span>
                          <span className={`text-sm font-medium ${irpfDescontadoEnNeto ? 'text-red-600' : 'text-gray-600'}`}>
                            {irpfDescontadoEnNeto ? '-' : ''} {formatCurrency(desglose.retencion_irpf, recibo.moneda)}
                          </span>
                        </div>
                        {!irpfDescontadoEnNeto && (
                          <p className="text-xs text-gray-500">
                            Documentada por separado. El neto histórico no descuenta esta retención
                            {desglose.neto_despues_retencion !== undefined && desglose.neto_despues_retencion !== null
                              ? `; neto luego de retención: ${formatCurrency(desglose.neto_despues_retencion, recibo.moneda)}`
                              : ''}.
                          </p>
                        )}
                      </div>
                    )}
                    {Array.isArray(desglose.otros_importes) && desglose.otros_importes.length > 0 && (
                      <div className="space-y-1">
                        {desglose.otros_importes.map((item: any, idx: number) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-sm text-gray-700">{item.descripcion}:</span>
                            <span className="text-sm font-medium text-red-600">
                              - {formatCurrency(item.valor, recibo.moneda)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-gray-300 mt-2 pt-2 flex justify-between">
                      <span className="text-sm font-semibold text-gray-900">Neto a Recibir:</span>
                      <span className="text-sm font-bold text-gray-900">
                        {receiptTotal}
                      </span>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Receipt Details */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Detalles del Recibo</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">ID Pago</p>
                <p className="text-sm font-medium text-gray-900">{recibo.pago_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Contrato</p>
                <p className="text-sm font-medium text-gray-900">
                  {recibo.contrato?.numero_contrato || `#${recibo.contrato_id}`}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fecha de Emisión</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(recibo.fecha_emision)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Emitido Por</p>
                <p className="text-sm font-medium text-gray-900">
                  {recibo.usuario_emisor
                    ? `${recibo.usuario_emisor.first_name} ${recibo.usuario_emisor.last_name}`
                    : `Usuario #${recibo.emitido_por}`
                  }
                </p>
              </div>
              {(recibo.tipo_pago === 'PROPIETARIO' || desglose?.agente_retencion_inquilino) && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Resguardo presentado</p>
                    <p className="text-sm font-medium text-gray-900">
                      {recibo.resguardo_confirmado ? 'Sí' : 'Pendiente de entrega'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Número de resguardo</p>
                    <p className="text-sm font-medium text-gray-900">
                      {recibo.resguardo_numero || '-'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {recibo.tipo_pago === 'INQUILINO' && desglose?.agente_retencion_inquilino && recibo.estado === 'ACTIVO' && hasPermission('receipts.write') && (
            <div className="border-t pt-4 space-y-3">
              <h4 className="font-medium">Resguardo del inquilino</h4>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={resguardoForm.confirmado} onChange={e => setResguardoForm(prev => ({ ...prev, confirmado: e.target.checked }))} />
                Resguardo presentado por el inquilino
              </label>
              <label className="block text-sm">Número de resguardo
                <Input value={resguardoForm.numero} maxLength={50} onChange={e => setResguardoForm(prev => ({ ...prev, numero: e.target.value }))} />
              </label>
              <Button disabled={submitting || (resguardoForm.confirmado && !resguardoForm.numero.trim())} onClick={saveResguardo}>Guardar resguardo</Button>
            </div>
          )}

          {/* Cancellation Info (if canceled) */}
          {recibo.estado === 'CANCELADO' && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Información de Cancelación</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Fecha de Cancelación</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(recibo.fecha_cancelacion)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cancelado Por</p>
                  <p className="text-sm font-medium text-gray-900">
                    {recibo.usuario_cancelador
                      ? `${recibo.usuario_cancelador.first_name} ${recibo.usuario_cancelador.last_name}`
                      : recibo.cancelado_por ? `Usuario #${recibo.cancelado_por}` : '-'
                    }
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">motivo de anulación</p>
                  <p className="text-sm font-medium text-gray-900">{recibo.motivo_cancelacion || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* CFE Documents Section */}
          {hasPermission('dgi', 'read') && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Facturación Electrónica (CFE)</h4>
                {recibo.estado === 'ACTIVO' && !cfeLoading && !cfeLoadError && cfeLoadedReceipt === Number(recibo.id) && cfeDocuments.length === 0 && hasPermission('dgi', 'write') && (
                  <Button onClick={handleGenerateCfe} disabled={cfeGenerating || cfeLoading}>
                    {cfeGenerating ? 'Generando...' : 'Generar CFE'}
                  </Button>
                )}
              </div>

              {/* CFE generation result banner */}
              {cfeGenerateResult && (
                <div className={`mb-3 rounded-md border p-3 ${
                  !cfeGenerateResult.success
                    ? 'bg-red-50 border-red-200'
                    : cfeGenerateResult.has_warnings
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-green-50 border-green-200'
                }`}>
                  <p className={`text-sm font-medium mb-1 ${
                    !cfeGenerateResult.success ? 'text-red-800' : cfeGenerateResult.has_warnings ? 'text-yellow-800' : 'text-green-800'
                  }`}>
                    {cfeGenerateResult.message}
                  </p>
                  {cfeGenerateResult.documents.length > 0 && (
                    <ul className="mt-1 space-y-1">
                      {cfeGenerateResult.documents.map((doc, i) => (
                        <li key={i} className="text-xs">
                          {doc.cfe_tipo && (
                            <span className="font-medium">{doc.cfe_tipo}: </span>
                          )}
                          {!doc.success ? (
                            <span className="text-red-700">
                              {doc.message}
                              {/* Show raw TA-FACE error if it carries extra detail */}
                              {doc.raw_error && doc.raw_error !== doc.message && (
                                <span className="block text-red-600 opacity-75 mt-0.5">
                                  Detalle: {doc.raw_error}
                                </span>
                              )}
                            </span>
                          ) : doc.warning ? (
                            <span className="text-yellow-700">{doc.warning}</span>
                          ) : (
                            <span className="text-green-700">{doc.message}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {cfeLoadError && (
                <div role="alert" className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 space-y-2">
                  <p className="text-sm text-red-800">{cfeLoadError}</p>
                  <p className="text-sm text-red-800">No se pudo comprobar la lista de CFE. La generación queda bloqueada hasta completar la consulta.</p>
                  <Button onClick={loadCfeDocuments} disabled={cfeLoading} variant="secondary">Reintentar carga de CFE</Button>
                </div>
              )}
              {cfeLoading || (!cfeLoadError && cfeLoadedReceipt !== Number(recibo.id)) ? (
                <p className="text-sm text-gray-500">Cargando...</p>
              ) : cfeDocuments.length === 0 ? (
                cfeLoadError ? null : <p className="text-sm text-gray-500">Sin documentos CFE asociados</p>
              ) : (
                <div className="space-y-2">
                  {cfeDocuments.map((cfe) => (
                    <div
                      key={cfe.id}
                      onClick={() => { setSelectedCfeDoc(cfe); setShowCfeDetail(true); }}
                      className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {CfeTipoLabel[cfe.cfe_tipo_code] || `Tipo ${cfe.cfe_tipo_code}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {cfe.cfe_serie && cfe.cfe_numero ? `${cfe.cfe_serie}-${cfe.cfe_numero}` : `Doc #${cfe.id}`}
                            {' - '}
                            {formatReceiptAmount(cfe.monto_total, cfe.moneda)}
                          </p>
                        </div>
                      </div>
                      <CfeStatusBadge cfe={cfe} showType={false} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex gap-2 mb-3">
              <Button onClick={handleGoToPago} variant="secondary">
                Ver Pago
              </Button>
              <Button onClick={handleGoToContrato} variant="secondary">
                Ver Contrato
              </Button>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleDownloadPDF}>
                Descargar PDF
              </Button>
              {recibo.estado === 'ACTIVO' && hasPermission('receipts', 'cancel') && (
                <Button onClick={() => setShowCancelModal(true)} variant="danger">
                  Anular Recibo
                </Button>
              )}
              <Button onClick={onClose} variant="secondary">
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* CFE Detail Modal */}
      <CfeDetailModal
        isOpen={showCfeDetail}
        onClose={() => setShowCfeDetail(false)}
        cfe={selectedCfeDoc}
        onCfeUpdated={loadCfeDocuments}
      />

      {/* Cancel Receipt Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Anular Recibo" size="md">
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              <strong>Advertencia:</strong> La anulación de un recibo implica la generación de una nota de crédito por igual monto que el documento original.
            </p>
          </div>
          <div>
            <Input
              label="Motivo de anulación"
              type="textarea"
              value={cancelMotivo}
              onChange={(e) => setCancelMotivo(e.target.value)}
              placeholder="Ingrese el motivo de la cancelación (mínimo 10 caracteres)..."
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              {cancelMotivo.length}/500 caracteres
              {cancelMotivo.trim().length < 10 && cancelMotivo.length > 0 && (
                <span className="text-red-600 ml-2">
                  (mínimo 10 caracteres requeridos)
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCancelRecibo} variant="danger" disabled={submitting}>
              {submitting ? 'Anulando...' : 'Confirmar Anulación'}
            </Button>
            <Button onClick={() => setShowCancelModal(false)} variant="secondary">
              Volver
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
